import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../client.js';

/** Kanban-Darstellungsmodus (T-071):
 *  - `per_ap`     = pro Arbeitspaket ein Board (Standard, bisheriges Verhalten)
 *  - `per_project`= ein projektweites Board (Summe aller AP-Aufgaben) */
export type KanbanBoardMode = 'per_ap' | 'per_project';

export const KANBAN_MODE_DEFAULT: KanbanBoardMode = 'per_project';

/** Mandanten-Standardmodus (tenant-scoped). Backend liefert ohne gesetzten Wert
 *  den Default `per_ap`. */
const KEY = ['kanban-settings'] as const;

export function useKanbanSettings() {
  return useQuery({
    queryKey: KEY,
    queryFn: () => apiClient.get('/api/v1/kanban-settings'),
    staleTime: 60_000,
  });
}

export function useUpdateKanbanSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { board_mode: KanbanBoardMode }) =>
      apiClient.patch('/api/v1/kanban-settings', data),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

/** Komfort: den Mandanten-Standardmodus direkt als Wert (mit Fallback). */
export function useTenantKanbanMode(): KanbanBoardMode {
  const { data } = useKanbanSettings();
  const mode = (data as { data?: { board_mode?: KanbanBoardMode } } | undefined)?.data?.board_mode;
  return mode ?? KANBAN_MODE_DEFAULT;
}

/** Effektiver Modus eines Projekts: die gespeicherte Benutzerwahl (Projekt-Override) gewinnt.
 *  Ohne Override gilt (PHQ-103) „ein Board pro Projekt". Der Mandanten-Standard des Backends
 *  liefert ohne gesetzten Wert `per_ap` und ist von einer bewussten Wahl nicht zu
 *  unterscheiden — er ist deshalb keine Quelle mehr. */
export function resolveKanbanMode(
  projectMode: KanbanBoardMode | null | undefined,
): KanbanBoardMode {
  return projectMode ?? KANBAN_MODE_DEFAULT;
}
